import React, { useState, useCallback } from 'react'
import { Box, Typography, Button, Alert } from '@mui/material'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import { useCreateTokenMutation } from '@/features/internal/patient-messaging/hooks/mutations/useCreateTokenMutation'
import { IntegrationToken } from '@/features/internal/patient-messaging/types/patientMessaging'
import LineLinkQRModal from './LineLinkQRModal'

interface LineLinkSectionProps {
  patientId: string
  isPharmacyConfigured: boolean
}

const LineLinkSection: React.FC<LineLinkSectionProps> = ({
  patientId,
  isPharmacyConfigured,
}) => {
  const [tokenData, setTokenData] = useState<IntegrationToken | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const createTokenMutation = useCreateTokenMutation()

  const handleShowQR = useCallback(async () => {
    try {
      const token = await createTokenMutation.mutateAsync(patientId)
      setTokenData(token)
      setModalOpen(true)
    } catch {
      // トークン生成失敗（薬局未設定等）はエラー表示のみ
    }
  }, [patientId, createTokenMutation])

  const handleCloseModal = useCallback(() => {
    setModalOpen(false)
  }, [])

  // 薬局LINE未設定
  if (!isPharmacyConfigured) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
          p: 3,
        }}
      >
        <Alert severity="info" sx={{ width: '100%' }}>
          LINE公式アカウントの設定が必要です。管理者に連絡してください。
        </Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: 2,
        p: 3,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ textAlign: 'center' }}
      >
        この患者とLINEで連携されていません。
        <br />
        QRコードを読み取ってもらうことで連携できます。
      </Typography>

      <Button
        variant="outlined"
        startIcon={<QrCode2Icon />}
        onClick={handleShowQR}
        disabled={createTokenMutation.isPending}
      >
        QRコードを表示
      </Button>

      {createTokenMutation.isError && (
        <Alert severity="error" sx={{ width: '100%' }}>
          QRコードの生成に失敗しました。しばらくしてから再度お試しください。
        </Alert>
      )}

      {tokenData && (
        <LineLinkQRModal
          open={modalOpen}
          onClose={handleCloseModal}
          tokenData={tokenData}
          patientId={patientId}
        />
      )}
    </Box>
  )
}

export default LineLinkSection
